import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, MapPin, CalendarDays, GraduationCap, Menu, Square, Database, X, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import "@/mockups/deepcoast.css";
import "@/components/ai2/ai2.css";
import { AI2, streamAsk } from "@/components/ai2/api";
import { ConversationList } from "@/components/ai2/ConversationList";
import { AssistantMessage, UserMessage } from "@/components/ai2/Messages";
import { QuestionLibrary } from "@/components/ai2/QuestionLibrary";
import { FeedbackDialog } from "@/components/ai2/FeedbackDialog";
import { SourceDialog } from "@/components/ai2/SourceDialog";
import { Evidence } from "@/components/ai2/Evidence";

const PERIODS = [["last30", "Last 30 days"], ["week", "Last 7 days"], ["mtd", "Month to date"], ["qtd", "Quarter to date"], ["ytd", "Year to date"], ["last90", "Last 90 days"]];

const Pills = ({ location, period, setPeriod, locked }) => (
  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }} data-testid="ai2-context-pills">
    <span className="ai2-pill" title="Change location from the top bar, or name another location in your question" data-testid="ai2-location-pill"><MapPin size={12} color="#1F8A7C" />{location}</span>
    <label className="ai2-pill" style={{ paddingRight: 4 }} data-testid="ai2-period-pill">
      <CalendarDays size={12} color="#1F8A7C" />
      <select value={period} disabled={locked} onChange={(e) => setPeriod(e.target.value)} style={{ border: 0, background: "transparent", fontSize: 12, outline: "none", cursor: "pointer" }} data-testid="ai2-period-select">
        {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </label>
  </div>
);

const Empty = ({ ctx, onAsk }) => (
  <div style={{ padding: "8px 4px 24px" }} data-testid="ai2-welcome">
    <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-0.02em", margin: "8px 0 6px" }}>Hi {ctx.user_name}, what do you want to know about {ctx.location}?</h1>
    <p className="muted" style={{ fontSize: 14, margin: "0 0 24px", maxWidth: 640 }}>I look up live FourPawIQ and Gingr data before answering, show you how I got each number, and learn from your corrections.</p>
    <QuestionLibrary isManager={ctx.is_manager} onAsk={onAsk} />
  </div>
);

export default function AIInsights2() {
  const [ctx, setCtx] = useState(null);
  const [convs, setConvs] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [live, setLive] = useState(null);
  const [status, setStatus] = useState("");
  const [period, setPeriod] = useState("last30");
  const [input, setInput] = useState("");
  const [sel, setSel] = useState(null);
  const [fb, setFb] = useState(null);
  const [source, setSource] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [hideList, setHideList] = useState(() => localStorage.getItem("ai2HideList") === "1");
  const [hideDetails, setHideDetails] = useState(false);
  useEffect(() => { localStorage.setItem("ai2HideList", hideList ? "1" : "0"); }, [hideList]);
  const endRef = useRef(null);
  const abortRef = useRef(null);
  const liveRef = useRef(null);
  useEffect(() => { liveRef.current = live; }, [live]);

  const loadConvs = useCallback(() => axios.get(`${AI2}/conversations`, { params: q ? { q } : {} }).then((r) => setConvs(r.data)).catch(() => {}), [q]);
  useEffect(() => { axios.get(`${AI2}/context`).then((r) => setCtx(r.data)).catch(() => toast.error("Couldn't load AI Insights")); }, []);
  useEffect(() => { const t = setTimeout(loadConvs, 250); return () => clearTimeout(t); }, [loadConvs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, live]);

  const open = async (id) => {
    setListOpen(false);
    const { data } = await axios.get(`${AI2}/conversations/${id}`);
    setActive(data);
    setMessages(data.messages);
    setPeriod(data.period || "last30");
    setSel([...data.messages].reverse().find((m) => m.role === "assistant" && m.evidence?.length)?.id || null);
  };
  const reset = () => { setActive(null); setMessages([]); setSel(null); setListOpen(false); };

  const ask = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || live) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: `u-${Date.now()}` }]);
    setLive({ content: "", charts: [] });
    setStatus("Thinking…");
    abortRef.current = new AbortController();
    try {
      await streamAsk({ message: msg, conversation_id: active?.id, period }, (ev) => {
        if (ev.type === "start" && !active) { setActive({ id: ev.conversation_id, title: ev.title, location_name: ev.location }); loadConvs(); }
        if (ev.type === "status") setStatus(ev.text);
        if (ev.type === "delta") { setStatus(""); setLive((l) => ({ ...l, content: l.content + ev.text })); }
        if (ev.type === "chart") setLive((l) => ({ ...l, charts: [...l.charts, ev.chart] }));
        if (ev.type === "done") {
          setMessages((m) => [...m, ev.message]);
          if (ev.message.evidence?.length) setSel(ev.message.id);
          loadConvs();
        }
      }, abortRef.current.signal);
    } catch (e) {
      if (e.name === "AbortError") {
        const partial = liveRef.current;
        setMessages((m) => [...m, { role: "assistant", id: `stopped-${Date.now()}`, local: true, content: `${partial?.content || ""}\n\n_Stopped._`, charts: partial?.charts || [] }]);
      } else toast.error(e.message || "Something went wrong");
    } finally {
      setLive(null);
      setStatus("");
    }
  };

  const rateUp = async (m) => {
    try {
      const { data } = await axios.post(`${AI2}/messages/${m.id}/feedback`, { rating: "up" });
      setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, rating: "up" } : x)));
      toast.success(data.status === "pending" ? "Thanks. Saved for a manager to approve as a trusted example." : "Thanks. Saved as a trusted example.");
    } catch (e) { toast.error("Couldn't save rating"); }
  };
  const onCorrected = (text, st) => {
    const m = fb;
    setFb(null);
    setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, rating: "down" } : x)));
    if (text && st === "active") ask(m.question);
  };

  if (!ctx) return <div className="dc v3" style={{ padding: 24 }}><div className="skeleton" style={{ height: 480, borderRadius: 12 }} data-testid="ai2-loading" /></div>;
  const selMsg = messages.find((m) => m.id === sel);
  const location = active?.location_name || ctx.location;

  return (
    <div className="dc v3 ai2" style={{ background: "transparent" }} data-testid="ai2-page">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button className="btn ai2-mobile-only" onClick={() => setListOpen(true)} aria-label="Conversations" data-testid="ai2-open-list"><Menu size={14} /></button>
        <button className="btn btn-sm ai2-desktop-only" onClick={() => setHideList(!hideList)} title={hideList ? "Show conversation history" : "Hide conversation history"} data-testid="ai2-toggle-list">
          {hideList ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
        </button>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>AI Insights</h2>
        <Pills location={location} period={period} setPeriod={setPeriod} locked={!!live} />
        {ctx.is_manager && <Link to="/ai-insights/learned" className="btn btn-sm" style={{ marginLeft: "auto", textDecoration: "none" }} data-testid="ai2-learned-link"><GraduationCap size={14} />What AI has learned</Link>}
      </div>
      <div className={`ai2-grid ${selMsg && !hideDetails ? "" : "no-details"} ${hideList ? "no-list" : ""}`}>
        <div className={`ai2-col ai2-list-col ${listOpen ? "open" : ""}`}>
          <ConversationList convs={convs} activeId={active?.id} q={q} setQ={setQ} onOpen={open} onNew={reset} onClose={() => setListOpen(false)}
            onChanged={(deleted) => { if (deleted && deleted === active?.id) reset(); loadConvs(); }} />
        </div>
        <div className="card ai2-col ai2-chat" style={{ padding: 0 }}>
          <div className="ai2-scroll" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, background: "var(--surface-raised)" }} data-testid="ai2-thread">
            {!messages.length && !live && <Empty ctx={ctx} onAsk={ask} />}
            {messages.map((m) => (m.role === "user" ? <UserMessage key={m.id} msg={m} /> : (
              <AssistantMessage key={m.id} msg={m} onFollow={ask} onRate={rateUp} onDown={setFb} onDetails={(id) => { setSel(id); setHideDetails(false); }} selected={sel === m.id && !hideDetails} onSource={setSource} />
            )))}
            {live && <AssistantMessage msg={live} streaming status={status} onSource={setSource} />}
            <div ref={endRef} />
          </div>
          <div className="ai2-composer">
            <textarea className="input" rows={1} placeholder={`Ask about leads, ${ctx.is_manager ? "revenue, " : ""}today's ops, packages, customers or procedures at ${location}…`} value={input}
              onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} data-testid="ai2-input" />
            {live ? <button className="btn" onClick={() => abortRef.current?.abort()} data-testid="ai2-stop"><Square size={14} />Stop</button>
              : <button className="btn btn-primary" onClick={() => ask()} disabled={!input.trim()} data-testid="ai2-send"><Send size={14} />Ask</button>}
          </div>
        </div>
        {selMsg && !hideDetails && (
          <div className="card ai2-col ai2-details-col" style={{ padding: 16 }} data-testid="ai2-details">
            <div className="label" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><Database size={12} color="#1F8A7C" />Answer details
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto", padding: 4 }} onClick={() => setHideDetails(true)} aria-label="Close answer details" data-testid="ai2-details-close"><X size={14} /></button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>How I got this: "{selMsg.question}"</div>
            <div className="ai2-scroll"><Evidence msg={selMsg} /></div>
          </div>
        )}
      </div>
      {fb && <FeedbackDialog msg={fb} isManager={ctx.is_manager} onClose={() => setFb(null)} onSaved={onCorrected} />}
      {source && <SourceDialog source={source} onClose={() => setSource(null)} />}
    </div>
  );
}
